import type { ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { zoneSslProblem } from '@cp/shared';
import { Banner } from '@/components/ui';
import { ZONE_SSL_LABELS } from '@/utils/labels';
import type { SiteDetail } from '@/types/api';

/**
 * The zone's Cloudflare SSL mode against the certificate on the node (P10),
 * judged by the rule in @cp/shared; full with a verifiable certificate could
 * be stricter.
 */
export function ZoneSsl({ mode, authenticated }: { readonly mode: string | null; readonly authenticated: boolean }): ReactNode {
  if (!mode) return <span className="faint">—</span>;
  const name = ZONE_SSL_LABELS[mode] ?? mode;
  const problem = zoneSslProblem(mode, authenticated);
  if (problem === 'loop') {
    return (
      <span className="tone-down" title="Cloudflare идёт на сервер по HTTP, сервер отвечает переходом на HTTPS — бесконечный редирект">
        {name} — нужен Full
      </span>
    );
  }
  if (problem === 'untrusted') {
    return (
      <span className="tone-down" title="Сертификат на сервере самоподписанный: Cloudflare ответит ошибкой 526">
        {name} — ошибка 526
      </span>
    );
  }
  if (mode === 'full' && authenticated) return <span title="Сертификат проверяемый: можно включить Full (strict)">{name}</span>;
  return <span className="tone-healthy">{name}</span>;
}

/** A zone mode the site cannot answer with through Cloudflare, said at the top of its page. */
export function ZoneSslBanner({ site }: { readonly site: SiteDetail }): ReactNode {
  const authenticated = site.originCertificate?.authenticated ?? false;
  const loop = site.domains.find((domain) => zoneSslProblem(domain.zoneSslMode, authenticated) === 'loop');
  const untrusted = site.domains.find((domain) => zoneSslProblem(domain.zoneSslMode, authenticated) === 'untrusted');
  if (!loop && !untrusted) return null;
  return (
    <Banner tone="danger" icon={ShieldAlert}>
      {loop
        ? `Зона ${loop.fqdn}: режим SSL Flexible вызывает цикл перенаправлений. Переключите режим на Full или Full (strict).`
        : `Зона ${untrusted!.fqdn}: режим Full (strict) при самоподписанном сертификате сервера вызывает ошибку 526. Переключите режим на Full.`}
    </Banner>
  );
}
